// src/pages/MealInsights.js
// Cross-event meal popularity dashboard. Caterer KPI page that only
// Eventory has the data to build — sits on top of Order.items aggregated
// across every event the account admins.
//
// Phase E+ analytics ride. Data already captured during normal ops; this
// is pure aggregation + render. No new schema, no marketplace inventory
// war. See docs/strategy/competitive-positioning.md.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const formatNumber = (n) => new Intl.NumberFormat('en-NG').format(Number(n || 0));

const MealInsights = () => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		let alive = true;
		(async () => {
			try {
				const res = await api.get('/account/meal-insights');
				if (alive) setData(res.data);
			} catch (err) {
				if (alive) setError(err.response?.data?.msg || 'Failed to load meal insights.');
			} finally {
				if (alive) setLoading(false);
			}
		})();
		return () => { alive = false; };
	}, []);

	const maxPlates = useMemo(() => {
		if (!data?.topMeals?.length) return 1;
		return Math.max(...data.topMeals.map((m) => m.plates), 1);
	}, [data]);

	return (
		<div className="ev-user-shell">
			<header className="ev-admin-topbar">
				<div className="ev-admin-topbar-inner">
					<div className="ev-admin-topbar-left">
						<div className="ev-brand-lockup">
							<img src="/images/eventory-logo.svg" alt="Eventory" />
						</div>
					</div>
					<div className="ev-admin-topbar-actions">
						<Link to="/dashboard" className="ev-soft-btn">My events</Link>
						<Link to="/account/team" className="ev-soft-btn">Team</Link>
						<Link to="/account/vendors" className="ev-soft-btn">Vendors</Link>
						<Link to="/account" className="ev-soft-btn">Account</Link>
					</div>
				</div>
			</header>

			<main className="ev-user-dash">
				<div className="ev-user-wrap">
					<div className="ev-admin-header">
						<div>
							<h2 className="ev-section-title">Meal insights</h2>
							<div className="ev-subtle">
								Your top dishes across every event you've run. Aggregated by name (case-insensitive) so the same dish at different events rolls up correctly.
							</div>
						</div>
					</div>

					{error && <div className="ev-alert ev-alert-danger">{error}</div>}
					{loading && <div className="ev-subtle">Loading…</div>}

					{!loading && data && (
						<>
							<div className="ev-overview-grid ev-bottom-gap">
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Events analysed</span></div>
									<div className="ev-metric-value">{formatNumber(data.totalEvents)}</div>
								</div>
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Orders placed</span></div>
									<div className="ev-metric-value">{formatNumber(data.totalOrders)}</div>
								</div>
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Plates served</span></div>
									<div className="ev-metric-value">{formatNumber(data.totalPlates)}</div>
								</div>
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Unique dishes</span></div>
									<div className="ev-metric-value">{formatNumber((data.topMeals || []).length)}</div>
									<div className="ev-subtle" style={{ fontSize: 12, marginTop: 4 }}>top 25 shown</div>
								</div>
							</div>

							{data.totalPlates === 0 ? (
								<section className="ev-admin-section ev-flat-card">
									<div className="ev-empty">
										<strong>No plates served yet.</strong>
										<div className="ev-subtle">As soon as guests place orders, your top dishes will show up here.</div>
									</div>
								</section>
							) : (
								<section className="ev-admin-section ev-flat-card">
									<h3 className="ev-zero-margin">Top dishes</h3>
									<div className="ev-subtle" style={{ marginBottom: 12 }}>
										Ranked by total plates served. Attach rate is the % of orders that included the dish.
									</div>
									<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
										{data.topMeals.map((meal, i) => {
											const barPct = Math.round((meal.plates / maxPlates) * 100);
											return (
												<div
													key={`${meal.name}-${i}`}
													style={{
														background: 'white',
														border: '1px solid rgba(17,17,19,0.06)',
														borderRadius: 10,
														padding: '12px 14px',
													}}
												>
													<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
														<div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
															<span style={{ fontSize: 12, color: '#6B7280', fontWeight: 700, minWidth: 22 }}>
																#{i + 1}
															</span>
															<strong style={{ fontSize: 15 }}>{meal.name}</strong>
															<span className="ev-subtle" style={{ fontSize: 12 }}>· {meal.course}</span>
														</div>
														<div className="ev-subtle" style={{ fontSize: 12 }}>
															{formatNumber(meal.plates)} plate{meal.plates === 1 ? '' : 's'} · {meal.attachRate}% attach · {meal.eventCount} event{meal.eventCount === 1 ? '' : 's'}
														</div>
													</div>
													<div style={{ height: 6, background: 'rgba(61, 30, 109, 0.08)', borderRadius: 999, overflow: 'hidden' }}>
														<div
															style={{
																width: `${barPct}%`,
																height: '100%',
																background: 'linear-gradient(90deg, #3D1E6D 0%, #FB8601 100%)',
																borderRadius: 999,
															}}
														/>
													</div>
												</div>
											);
										})}
									</div>
								</section>
							)}
						</>
					)}
				</div>
			</main>
		</div>
	);
};

export default MealInsights;
